// hooks/useCrewManagement.js
import { useMemo, useCallback } from 'react';
import { 
  filterCrews, 
  calculateCrewDayHours, 
  calculateCrewWeeklyHours,
  sortCrews 
} from '../utils/crewUtils';
import { selectCrew, updateFilters, setHighlightedStop } from '../state/routeActions';

export const useCrewManagement = ({ crews, weeklyHours, filters, dispatch }) => {
  // Filter crews based on search term
  const filteredCrews = useMemo(() => {
    // console.log('filterCrews called...', filters, crews)
    if (!crews) return []
    const filtered = filterCrews(crews, filters.searchTerm);
    // console.log('Filtered crews 15:', filtered)
    return sortCrews(filtered, filters.sortMethod, filters.sortDirection);
  }, [crews, filters.searchTerm, filters.sortMethod, filters.sortDirection]);
  
  // Get selected crew from filters
  const selectedCrew = useMemo(() => {
    return filters.selectedCrew;
  }, [filters.selectedCrew]);
  
  // Handle crew selection
  const handleCrewSelect = useCallback((crew) => {
    console.log('28...handleCrewSelect', crew)
    dispatch(selectCrew(crew));
    dispatch(setHighlightedStop(null));
  }, [dispatch]);
  
  // Navigate between crews
  const handleCrewNavigation = useCallback((direction) => {
    if (!selectedCrew || !filteredCrews.length) return;
    
    const currentIndex = filteredCrews.findIndex(crew => crew._id === selectedCrew._id);
    if (currentIndex === -1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex - 1;
      if (newIndex < 0) newIndex = filteredCrews.length - 1;
    } else {
      newIndex = currentIndex + 1;
      if (newIndex >= filteredCrews.length) newIndex = 0;
    }
    
    const newCrew = filteredCrews[newIndex];
    if (newCrew) {
      dispatch(selectCrew(newCrew));
    }
  }, [selectedCrew, filteredCrews, dispatch]);
  
  // Calculate crew hours for a specific day
  const calculateCrewHours = useCallback((crewId, dayOfWeek) => {
    return calculateCrewDayHours(weeklyHours, crewId, dayOfWeek);
  }, [weeklyHours]);
  
  // Calculate crew weekly totals
  const calculateCrewWeeklyTotals = useCallback((crewId) => {
    return calculateCrewWeeklyHours(weeklyHours, crewId);
  }, [weeklyHours]);
  
  // Get crew by ID
  const getCrewById = useCallback((crewId) => {
    return crews.find(crew => crew._id === crewId);
  }, [crews]);
  
  // Sort crews
  const handleSortCrews = useCallback((method) => {
    const newDirection = 
      filters.sortMethod === method && filters.sortDirection === 'asc' 
        ? 'desc' 
        : 'asc';
    
    dispatch(updateFilters({
      sortMethod: method,
      sortDirection: newDirection
    }));
  }, [filters.sortMethod, filters.sortDirection, dispatch]);
  
  // Check if crew is available for additional work
  const isCrewAvailable = useCallback((crewId, dayOfWeek, additionalHours) => {
    const currentHours = calculateCrewHours(crewId, dayOfWeek);
    const totalWithNew = currentHours.total + additionalHours;
    return totalWithNew <= 8; // 8 hour daily limit
  }, [calculateCrewHours]);
  
  // Find available crews for a job
  const findAvailableCrews = useCallback((job, dayOfWeek) => {
    return filteredCrews.filter(crew => 
      isCrewAvailable(crew._id, dayOfWeek, job.estimatedHours)
    );
  }, [filteredCrews, isCrewAvailable]);
  
  // Get crew stats
  const getCrewStats = useCallback((crewId) => {
    const weeklyTotals = calculateCrewWeeklyTotals(crewId);
    const utilization = (weeklyTotals.total / 40) * 100; // 40 hour work week
    
    return {
      weeklyHours: weeklyTotals.total,
      weeklyService: weeklyTotals.service,
      weeklyDrive: weeklyTotals.drive,
      utilization: Math.round(utilization),
      efficiency: weeklyTotals.total > 0 
        ? Math.round((weeklyTotals.service / weeklyTotals.total) * 100)
        : 0
    };
  }, [calculateCrewWeeklyTotals]);
  
  // Get all crews stats
  const getAllCrewsStats = useCallback(() => {
    return crews.map(crew => ({
      crew,
      stats: getCrewStats(crew._id)
    }));
  }, [crews, getCrewStats]);
  
  return {
    // State
    filteredCrews,
    selectedCrew,
    
    // Actions
    handleCrewSelect,
    handleCrewNavigation,
    handleSortCrews,
    
    // Calculations
    calculateCrewHours,
    calculateCrewWeeklyTotals,
    getCrewById,
    isCrewAvailable,
    findAvailableCrews,
    getCrewStats,
    getAllCrewsStats
  };
};